import { IField } from '@hooks/useField';
import useFormatter from '@hooks/useFormatter';
import { TextField, TextFieldProps } from '@material-ui/core';
import React from 'react';
import InputMask, { InputState, MaskOptions } from 'react-input-mask';

export type TelefoneMaskType = "tel" | "cel" | "both";

const masks = {
    "tel": "(99)9999-9999",
    "cel": "(99)9.9999-9999"
}

type TelefoneFieldProps = TextFieldProps & {
    maskType: TelefoneMaskType

    field: IField
    updateFieldStateOnChange?: boolean
    onValueChanged?: (newValue: any) => void
    onEnter?: React.KeyboardEventHandler<HTMLDivElement> | undefined
}

function TelefoneFieldComponent ({ 
    disabled, 
    onChange, 
    maskType, 
    field, 
    onValueChanged, 
    onEnter,
    onKeyDown,
    updateFieldStateOnChange, 
    fullWidth,
    helperText,
    ...props 
}: TelefoneFieldProps) {
    const formatter = useFormatter();

    const [mask, setMask] = React.useState<string>(maskType === "both" ? (formatter.extractNumbers(field.value).length <= 10 ? masks.tel : masks.cel) : masks[maskType]);
    const [_value, _setValue] = React.useState(field.value);

    const handleChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const v = e.target.value;

        if (v !== _value) {
            _setValue(v);            
        }

    }, [_value]);

    const handleKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Enter" && onEnter !== undefined) {
            onEnter(e);
        }

        if (onKeyDown !== undefined) {
            onKeyDown(e);
        }
    }, [onEnter, onKeyDown]);

    const handleBeforeInput = React.useCallback((newState: InputState, oldState: InputState, userInput: string, maskOptions: MaskOptions,) => {
        if (maskType === "both") {
            let newMask: string | undefined = undefined;

            if (mask !== masks.cel && userInput?.length > masks.tel.length) {
                const lastPosition = masks.cel.length;
    
                newMask = masks.cel;
                newState.value = userInput;
                newState.selection = {
                    start: lastPosition,
                    end: lastPosition
                }
            }
            else {
                const oldLength = (oldState.value || "").toString().length;
                const newLength = (newState.value || "").toString().length;
        
                if (userInput !== null && mask !== masks.cel && oldLength === masks.tel.length) { //então ele quer escrever mais um caracter, portanto passar para a próxima máscara imediatamente maior
                    newMask = masks.cel;
                    newState.value += userInput;
    
                    const lastPosition = masks.cel.length;
    
                    newState.selection = {
                        start: lastPosition,
                        end: lastPosition
                    }
                }
                else if (oldState.value !== "" && mask !== masks.tel && newLength < masks.cel.length) {
                    newMask = masks.tel;
                }
            }

            if (newMask) {
                setMask(newMask);
            }
        }

        return newState;
    }, [mask, maskType]);

    /*
    const setFormattedValue = React.useCallback((value) => {
        const phone = formatter.toTelefone(value);

        if (maskType === "both") {
            const newMask = phone.length <= masks.tel.length ? masks.tel : masks.cel;

            if (newMask !== mask) {
                setMask(newMask);
            }
        }

        _setValue(phone);        
    }, [formatter, mask, maskType]);
    */

    //Reage à mudança do valor interno
    //Set o valor na ref
    //Chama o evento o change externo
    //Verifica se é para subir o estado automaticamente através da propriedade updateFieldStateOnChange
    React.useEffect(() => {
        if (mask !== masks.cel && _value?.length > masks.tel.length) {
            setMask(masks.cel);
        }

        field.setInputValue(_value);

        if (onValueChanged !== undefined) {
            onValueChanged(_value);
        }

        if (updateFieldStateOnChange === true && field.value !== _value) {
            field.setValue(_value);
        }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [_value, onValueChanged, updateFieldStateOnChange]);

    //Reage à mudança do valor externo
    //Atualiza o valor interno pelo valor externo
    React.useEffect(() => {
        if (field.value !== _value) {
            _setValue(field.value);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [field.value]);

    React.useEffect(() => {
        if (field.transition === "reset") {
            _setValue(field.initialValueRef?.current);
            field.clearTransition();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [field.transition]);

    return (
        <InputMask
            mask={mask}
            maskChar={null}
            alwaysShowMask={false}
            readOnly={props.InputProps?.readOnly}
            value={_value || ""}
            beforeMaskedValueChange={handleBeforeInput}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            disabled={disabled}
        >
            {() => 
                <TextField
                    {...props}
                    disabled={disabled}
                    name={field.name}
                    value={_value !== undefined && _value !== null ? _value : ""}
                    fullWidth={fullWidth !== false}
                    helperText={!!field.error ? field.error : helperText}
                    error={!!field.error}
                />
        }
        </InputMask>
    );

}
export const TelefoneField = React.memo(TelefoneFieldComponent);